import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
  type ReactNode, type RefObject,
} from 'react';
import { IconClose } from './icons';
import { pushBackHandler } from '@/lib/native';

// --- toasts ---------------------------------------------------------------

interface Toast { id: number; body: string; tone: 'default' | 'error' }

const ToastContext = createContext<(body: string, tone?: Toast['tone']) => void>(() => {});

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);

  const push = useCallback((body: string, tone: Toast['tone'] = 'default') => {
    const id = nextId.current;
    nextId.current += 1;
    setToasts((prev) => [...prev.slice(-2), { id, body, tone }]);
    window.setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 3600);
  }, []);

  return (
    <ToastContext.Provider value={push}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`toast${t.tone === 'error' ? ' toast--error' : ''}`}>{t.body}</div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export const useToast = () => useContext(ToastContext);

// --- modal ----------------------------------------------------------------

export function Modal({
  title, onClose, children, footer, wide = false,
}: {
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    // Android's back button should close the dialog, exactly like Escape does.
    // Hooking it here means every modal in the app gets it for free.
    const release = pushBackHandler(() => { onClose(); return true; });
    return () => {
      window.removeEventListener('keydown', onKey);
      release();
    };
  }, [onClose]);

  return (
    <div className="backdrop" onPointerDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={`modal${wide ? ' modal--wide' : ''}`} role="dialog" aria-modal="true">
        <div className="modal__head">
          <div className="modal__title grow">{title}</div>
          <button type="button" className="pill-btn pill-btn--sm" onClick={onClose} aria-label="Close">
            <IconClose size={20} />
          </button>
        </div>
        <div className="modal__body">{children}</div>
        {footer ? <div className="modal__foot">{footer}</div> : null}
      </div>
    </div>
  );
}

// --- small pieces ---------------------------------------------------------

export function Toggle({
  on, onChange, label, hint, disabled = false,
}: {
  on: boolean; onChange: (next: boolean) => void; label: string; hint?: string; disabled?: boolean;
}) {
  return (
    <div className="setting">
      <div className="setting__text">
        <div className="setting__label">{label}</div>
        {hint ? <div className="setting__hint">{hint}</div> : null}
      </div>
      <button
        type="button"
        className="toggle"
        data-on={on}
        disabled={disabled}
        role="switch"
        aria-checked={on}
        aria-label={label}
        onClick={() => !disabled && onChange(!on)}
      />
    </div>
  );
}

export function Alert({ tone, children }: { tone: 'error' | 'ok' | 'info'; children: ReactNode }) {
  return <div className={`alert alert--${tone}`}>{children}</div>;
}

/** Call `handler` when a pointer goes down outside every ref passed in. */
export function useDismiss(refs: Array<RefObject<HTMLElement>>, handler: () => void, active = true) {
  const saved = useRef(handler);
  saved.current = handler;
  useEffect(() => {
    if (!active) return undefined;
    const onDown = (e: PointerEvent) => {
      const target = e.target as Node;
      for (const ref of refs) if (ref.current?.contains(target)) return;
      saved.current();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') saved.current(); };
    // `capture` so the popover closes even when the canvas swallows the event.
    window.addEventListener('pointerdown', onDown, true);
    window.addEventListener('keydown', onKey);
    // An open popover is the first thing back should close, before the screen
    // behind it goes anywhere.
    const release = pushBackHandler(() => { saved.current(); return true; });
    return () => {
      window.removeEventListener('pointerdown', onDown, true);
      window.removeEventListener('keydown', onKey);
      release();
    };
  }, [active, refs]);
  return undefined;
}

/**
 * State backed by localStorage, merged over defaults so new keys appear.
 *
 * Keys moved from an `inkwell.` prefix to `inkshare.` when the app was renamed;
 * anything stored under the old name is carried over on first read so nobody
 * loses their tool and paper preferences.
 */
export function useStoredState<T extends object>(key: string, initial: T) {
  const [value, setValue] = useState<T>(() => {
    try {
      let raw = localStorage.getItem(key);
      if (!raw && key.startsWith('inkshare.')) {
        const legacy = localStorage.getItem(key.replace('inkshare.', 'inkwell.'));
        if (legacy) {
          localStorage.setItem(key, legacy);
          localStorage.removeItem(key.replace('inkshare.', 'inkwell.'));
          raw = legacy;
        }
      }
      return raw ? { ...initial, ...JSON.parse(raw) } : initial;
    } catch {
      return initial;
    }
  });

  const update = useCallback((patch: Partial<T>) => {
    setValue((prev) => {
      const next = { ...prev, ...patch };
      try { localStorage.setItem(key, JSON.stringify(next)); } catch { /* quota or private mode */ }
      return next;
    });
  }, [key]);

  return [value, update] as const;
}

export function relativeTime(at: number) {
  const diff = Date.now() - at;
  const mins = Math.round(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function initials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** Copy to clipboard with a fallback for insecure contexts. */
export async function copyText(text: string) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const el = document.createElement('textarea');
      el.value = text;
      el.style.position = 'fixed';
      el.style.opacity = '0';
      document.body.appendChild(el);
      el.select();
      const ok = document.execCommand('copy');
      el.remove();
      return ok;
    } catch {
      return false;
    }
  }
}

export function useIsMobile() {
  const [mobile, setMobile] = useState(() => window.innerWidth < 640);
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < 640);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return mobile;
}

/** Stable ref array for `useDismiss`. */
export function useRefList(count: number) {
  return useMemo(
    () => Array.from({ length: count }, () => ({ current: null }) as RefObject<HTMLElement>),
    [count],
  );
}
